export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
}

export function assertSafeInteger(value: unknown, label: string, minimum = Number.MIN_SAFE_INTEGER): asserts value is number {
  if (!Number.isSafeInteger(value) || Object.is(value, -0) || (value as number) < minimum) {
    throw new Error(`${label}: expected safe integer`);
  }
}

export function checkedAdd(left: number, right: number, label = "integer addition"): number {
  assertSafeInteger(left, label); assertSafeInteger(right, label);
  const result = BigInt(left) + BigInt(right);
  if (result < BigInt(Number.MIN_SAFE_INTEGER) || result > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error(`${label}: overflow`);
  return Number(result);
}

export function checkedMultiply(left: number, right: number, label = "integer multiplication"): number {
  assertSafeInteger(left, label); assertSafeInteger(right, label);
  const result = BigInt(left) * BigInt(right);
  if (result < BigInt(Number.MIN_SAFE_INTEGER) || result > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error(`${label}: overflow`);
  return Number(result);
}

export function assertExactKeys(value: unknown, keys: readonly string[], label: string): asserts value is Record<string, unknown> {
  if (!isPlainRecord(value)) throw new Error(`${label}: expected plain record`);
  const actual = Reflect.ownKeys(value);
  if (actual.length !== keys.length || actual.some((key, index) => typeof key !== "string" || key !== keys[index])) {
    throw new Error(`${label}: noncanonical fields`);
  }
}

export function copyAndFreezePlainData<T>(value: T): Readonly<T> {
  const seen = new Set<object>();
  const copy = (input: unknown): unknown => {
    if (input === null || typeof input === "string" || typeof input === "boolean") return input;
    if (typeof input === "number") {
      if (!Number.isFinite(input) || Object.is(input, -0)) throw new Error("plain data: invalid number");
      return input;
    }
    if (typeof input !== "object") throw new Error("plain data: unsupported value");
    if (seen.has(input)) throw new Error("plain data: cycle");
    seen.add(input);
    let result: unknown;
    if (Array.isArray(input)) {
      if (Object.getPrototypeOf(input) !== Array.prototype) throw new Error("plain data: non-plain array");
      const output: unknown[] = [];
      for (let index = 0; index < input.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(input, String(index));
        if (descriptor === undefined || !("value" in descriptor) || descriptor.enumerable !== true) {
          throw new Error("plain data: sparse or accessor array");
        }
        output.push(copy(descriptor.value));
      }
      if (Reflect.ownKeys(input).length !== input.length + 1) throw new Error("plain data: extra array property");
      result = Object.freeze(output);
    } else {
      if (Object.getPrototypeOf(input) !== Object.prototype) throw new Error("plain data: non-plain record");
      const output: Record<string, unknown> = {};
      for (const key of Reflect.ownKeys(input)) {
        if (typeof key !== "string") throw new Error("plain data: symbol key");
        const descriptor = Object.getOwnPropertyDescriptor(input, key);
        if (descriptor === undefined || !("value" in descriptor) || descriptor.enumerable !== true) {
          throw new Error("plain data: accessor or hidden property");
        }
        output[key] = copy(descriptor.value);
      }
      result = Object.freeze(output);
    }
    seen.delete(input);
    return result;
  };
  return copy(value) as Readonly<T>;
}
